import React, { useState, useEffect, useContext, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PaymentDetail from "./PaymentDetail";
import { getOpenIdFromLocation } from "../payments/__openIdNavigateHelper";
import { db } from "@/lib/firebase";
import { collection, onSnapshot, orderBy, query, where, doc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { ToastContext } from "@/components/ToastProvider";
import useUserProfile from "@/hooks/useUserProfile";
import useCoopMemberRole from "@/hooks/useCoopMemberRole";
import { formatDT } from "@/utils/dates";

function peso(n) { 
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); 
}

// Button component for consistent styling
const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  onClick, 
  className = '',
  ...props 
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500',
    outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-indigo-500'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };
  
  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;
  
  return (
    <button className={classes} disabled={disabled} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

// Badge component for consistent status display
const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-800 border-gray-200',
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    danger: 'bg-red-100 text-red-800 border-red-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200'
  };
  
  const classes = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variants[variant]} ${className}`;
  
  return <span className={classes}>{children}</span>;
};

function Section({ title, filter, actions, openId, adminUid, onOpenPayment }) {
  const { notify } = useContext(ToastContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [deliverySettings, setDeliverySettings] = useState(null);
  const navigate = useNavigate();
  
  // Fetch delivery settings
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "delivery"), (s) =>
      setDeliverySettings(s.data() || null)
    );
    return () => unsub();
  }, []);

  // Calculate delivery fee using same logic as Orders page
  const calculateDeliveryFee = useCallback((order) => {
    // Only apply delivery fee for delivery orders
    const isDeliveryOrder = order.method === "cod" || 
                           (order.method === "manual" && order.deliveryMode === "delivery") ||
                           order.deliveryMode === "delivery";
    
    if (!isDeliveryOrder) return 0;
    
    // Get delivery settings from database - handle both nested and flat structures
    const deliveryConfig = deliverySettings?.settings || deliverySettings || {};
    
    // Check if delivery is enabled
    if (deliveryConfig.enabled === false) return 0;
    
    // Check free delivery threshold - use the actual setting from database
    const freeThreshold = deliveryConfig.freeDeliveryThreshold;
    const subtotal = order.subtotal || 0;
    
    if (freeThreshold && subtotal >= freeThreshold) {
      return 0;
    }
    
    // Calculate fee based on delivery fee type
    if (deliveryConfig.deliveryFeeType === 'fixed') {
      const fee = deliveryConfig.fixedFee;
      if (fee && fee > 0) {
        return fee;
      } else {
        return 0;
      }
    } else if (deliveryConfig.deliveryFeeType === 'percentage') {
      const fee = deliveryConfig.percentageFee;
      if (fee && fee > 0) {
        return (subtotal * fee) / 100;
      } else {
        return 0;
      }
    } else if (deliveryConfig.deliveryFeeType === 'distance') {
      // For distance-based, use base fee for now
      const fee = deliveryConfig.baseFee;
      if (fee && fee > 0) {
        return fee;
      } else {
        return 0;
      }
    } else if (deliveryConfig.deliveryFeeType === 'zones') {
      // For zones-based delivery, calculate based on delivery zones
      if (!deliveryConfig.deliveryZones || !Array.isArray(deliveryConfig.deliveryZones)) {
        const fee = deliveryConfig.baseFee;
        if (fee && fee > 0) {
          return fee;
        }
        return 0;
      }
      
      // For now, use the first zone fee as default
      const defaultZoneFee = deliveryConfig.deliveryZones[0]?.fee;
      if (defaultZoneFee && defaultZoneFee > 0) {
        return defaultZoneFee;
      } else {
        const fee = deliveryConfig.baseFee;
        if (fee && fee > 0) {
          return fee;
        }
        return 0;
      }
    }
    // No delivery fee type configured
    return 0;
  }, [deliverySettings]);

  // Calculate weight-based fee using same logic as Orders page
  const calculateWeightBasedFee = useCallback((order) => {
    if (!deliverySettings) return 0;
    
    const deliveryConfig = deliverySettings?.settings || deliverySettings || {};
    const method = String(order.method || '').toLowerCase();
    const deliveryMode = String(order.deliveryMode || '').toLowerCase();
    // Only apply to delivery (cod or prepaid) orders, never pickup
    if (method === 'pickup' || deliveryMode === 'pickup') return 0;
    const isDeliveryOrder = (method === 'cod' || method === 'prepaid') || deliveryMode === 'delivery';
    if (!isDeliveryOrder) return 0;
    
    // Check if weight-based fees are enabled
    if (!deliveryConfig.weightBasedFees?.enabled) return 0;
    
    const orderWeight = order.orderWeight || 0;
    if (orderWeight <= 0) return 0;
    
    const { overweightThreshold, overweightFee } = deliveryConfig.weightBasedFees;
    
    // Validate that required settings exist
    if (!overweightThreshold || !overweightFee) return 0;
    
    if (orderWeight > overweightThreshold) {
      const overweight = orderWeight - overweightThreshold;
      return overweight * overweightFee;
    }
    
    return 0;
  }, [deliverySettings]);

  useEffect(() => {
    setLoading(true);
    try {
      const q = query(collection(db, "storePayments"), ...filter, orderBy("createdAt", "desc"));
      const unsub = onSnapshot(q, (snap) => { 
        setRows(snap.docs.map(d => ({ id: d.id, ...d.data() }))); 
        setLoading(false); 
      }, (e) => { 
        setErr(e?.message || String(e)); 
        setLoading(false); 
      });
      return () => unsub();
    } catch (e) { 
      setErr(String(e)); 
      setLoading(false); 
    }
  }, [JSON.stringify(filter)]);
  
  async function confirmHandover(row) {
    try {
      const fn = httpsCallable(functions, "confirmCodHandover");
      await fn({ orderId: row.orderId, storePaymentId: row.id, paymentId: row.paymentId || null });
      notify("Handover confirmed.", { type: "success" });
    } catch (e) { 
      notify(e?.message || String(e), { type: "error" }); 
    }
  }
  
  function openVerify(row) {
    const pid = row?.paymentId;
    if (pid && onOpenPayment) onOpenPayment(pid);
  }
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'completed':
        return <Badge variant="success">Completed</Badge>;
      case 'pending_handover':
        return <Badge variant="info">Pending Handover</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };
  
  return (
    <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      
      {err && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{err}</div>
            </div>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="p-8 text-center">
          <div className="inline-flex items-center px-4 py-2 text-sm text-gray-500">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center">
          <div className="text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-lg font-medium text-gray-900 mb-2">No records found</p>
            <p className="text-sm text-gray-600">There are no store payments matching the current filter.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block">
            <div className="card p-0">
              <div className="overflow-x-auto max-w-full">
                <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buyer</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verify</th>
                  {actions && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((r) => (
                  <tr key={r.id} className={`hover:bg-gray-50 ${openId === r.id ? 'bg-orange-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {r.createdAt?.toDate ? formatDT(r.createdAt.toDate()) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {r.orderId || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {r.buyerName || r.buyerUid || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      ₱{peso(r.subtotal)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {deliverySettings ? (
                        calculateDeliveryFee(r) === 0 ? (
                          <span className="text-green-600 font-medium">FREE</span>
                        ) : (
                          `₱${peso(calculateDeliveryFee(r))}`
                        )
                      ) : (
                        r.deliveryFee > 0 ? `₱${peso(r.deliveryFee)}` : r.deliveryFeeType === 'free' ? 'FREE' : '₱0.00'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {deliverySettings ? 
                        `₱${peso((r.subtotal || 0) + calculateDeliveryFee(r) + calculateWeightBasedFee(r))}` : 
                        `₱${peso(r.grandTotal)}`
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(r.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openVerify(r)}
                        disabled={!r.paymentId}
                        title={r.paymentId ? 'Open verification' : 'No payment record yet'}
                      >
                        Verify
                      </Button>
                    </td>
                    {actions && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {actions === 'cod' ? (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={r.status !== 'pending_handover'}
                              onClick={() => confirmHandover(r)}
                            >
                              Confirm Handover
                            </Button>
                          </div>
                        ) : null}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile list */}
          <div className="sm:hidden space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="card p-4">
                {/* Header with Order ID and Status */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm break-all">Order {r.orderId || '—'}</div>
                    <div className="text-xs text-gray-500 mt-1">{r.createdAt?.toDate ? formatDT(r.createdAt.toDate()) : '-'}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(r.status)}
                  </div>
                </div>
                
                {/* Payment Details */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-gray-500">Buyer</p>
                    <p className="text-sm font-medium text-gray-900">{r.buyerName || r.buyerUid || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-sm font-medium text-gray-900">
                      {deliverySettings ? 
                        `₱${peso((r.subtotal || 0) + calculateDeliveryFee(r) + calculateWeightBasedFee(r))}` : 
                        `₱${peso(r.grandTotal)}`
                      }
                    </p>
                  </div>
                </div>
                
                {/* Price Breakdown */}
                <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                  <div>
                    <p className="text-gray-500">Subtotal</p>
                    <p className="font-medium">₱{peso(r.subtotal)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Delivery</p>
                    <p className="font-medium">
                      {deliverySettings ? (
                        calculateDeliveryFee(r) === 0 ? (
                          <span className="text-green-600 font-medium">FREE</span>
                        ) : (
                          `₱${peso(calculateDeliveryFee(r))}`
                        )
                      ) : (
                        r.deliveryFee > 0 ? `₱${peso(r.deliveryFee)}` : r.deliveryFeeType === 'free' ? 'FREE' : '₱0.00'
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total</p>
                    <p className="font-medium">
                      {deliverySettings ? 
                        `₱${peso((r.subtotal || 0) + calculateDeliveryFee(r) + calculateWeightBasedFee(r))}` : 
                        `₱${peso(r.grandTotal)}`
                      }
                    </p>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openVerify(r)}
                    disabled={!r.paymentId}
                    title={r.paymentId ? 'Open verification' : 'No payment record yet'}
                  >
                    Verify
                  </Button>
                  
                  {actions === 'cod' && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={r.status !== 'pending_handover'}
                      onClick={() => confirmHandover(r)}
                    >
                      Confirm Handover
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function StorePayments() {
  const { profile } = useUserProfile();
  const { isFinancial } = useCoopMemberRole();
  const { notify } = useContext(ToastContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isStaff = isFinancial;
  const myUid = profile?.uid || profile?.id || null;
  
  const [openPaymentId, setOpenPaymentId] = useState("");

  if (!isStaff) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
        <p className="text-gray-600">You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Section
        title="All Store Payments"
        filter={[]}
        actions={null}
        openId={getOpenIdFromLocation(location)}
        adminUid={myUid}
        onOpenPayment={setOpenPaymentId}
      />
      
      <Section
        title="COD Orders Pending Handover"
        filter={[where("status", "==", "pending_handover")]}
        actions="cod"
        openId={getOpenIdFromLocation(location)}
        adminUid={myUid}
        onOpenPayment={setOpenPaymentId}
      />

      {/* Payment Detail Modal */}
      {openPaymentId && (
        <PaymentDetail
          id={openPaymentId}
          onClose={() => setOpenPaymentId("")}
          adminUid={myUid}
        />
      )}
    </div>
  );
}
